import { zodResolver } from "@hookform/resolvers/zod";
import { Globe, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { ActionCell } from "@/components/shared/action-cell";
import { ConfigFormDialog } from "@/components/shared/config-form-dialog";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DataTable } from "@/components/shared/data-table";
import { DataToolbar } from "@/components/shared/data-toolbar";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { HideRetiredFilter } from "@/components/shared/hide-retired-filter";
import { ReorderButtons } from "@/components/shared/reorder-buttons";
import { StatusPill } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useRetiredRows } from "@/hooks/use-retired-rows";
import {
  useCreateWorkLocation,
  useDeleteWorkLocation,
  useReorderWorkLocation,
  useUpdateWorkLocation,
  useWorkLocations,
  type WorkLocation,
} from "@/hooks/use-work-locations";
import {
  isProtectedWorkLocationKey,
  type WorkLocationCreateInput,
  workLocationCreateSchema,
} from "@/schemas/work-locations";

type DialogState =
  | { type: "none" }
  | { type: "create" }
  | { type: "edit"; location: WorkLocation }
  | { type: "delete"; location: WorkLocation };

/**
 * Settings → Config → Work locations: the coarse location bucket a person is
 * based in (U.S. / International at the time of writing).
 *
 * The plainest of the four config lists — no NOS column (location is on
 * `EXCLUDED_FIELDS`, since NOS stores free text) and nothing branches on a
 * specific value. Worth noting that this is the only one of them an ordinary
 * employee can set on themselves, via the self-service contact dialog.
 */
export function SettingsWorkLocationsPage() {
  const { data: rows = [], isLoading, isError, error, refetch } = useWorkLocations();
  const [dialog, setDialog] = useState<DialogState>({ type: "none" });
  const deleteRow = useDeleteWorkLocation();
  const reorder = useReorderWorkLocation();
  const { hideRetired, setHideRetired, active, hiddenCount, visible } = useRetiredRows(rows);

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load role locations"}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-sm text-muted-foreground">
        The broad work location a person is based in, chosen on their profile — and the one contact
        field people can set for themselves. The order here is the order they're offered in.
        Retiring one hides it from the pickers but keeps it readable on anyone who already has it
        and in their change history; a location can only be deleted while nobody holds it. This is
        deliberately coarse and is{" "}
        <span className="font-medium text-foreground">never sent to NOS</span>, which records a
        free-text city instead.
      </p>

      <DataToolbar
        filters={
          <HideRetiredFilter
            id="work-locations-hide-retired"
            checked={hideRetired}
            onCheckedChange={setHideRetired}
            hiddenCount={hiddenCount}
          />
        }
        actions={
          <Button onClick={() => setDialog({ type: "create" })}>
            <Plus className="mr-2 h-4 w-4" />
            Add location
          </Button>
        }
      />

      <DataTable
        headers={[
          { label: "Order", className: "w-24" },
          { label: "Name" },
          { label: "People" },
          { label: "Status" },
          { label: "Actions", className: "text-right" },
        ]}
        loading={isLoading}
        empty={
          <EmptyState
            icon={Globe}
            title="No role locations"
            description="The four defaults are seeded on server startup."
          />
        }
      >
        {visible.map((row, index) => (
          <tr
            key={row.id}
            className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
          >
            <td className="px-4 py-3">
              {/* A retired row is pinned to the bottom by the API ordering, so its
                  position carries no meaning and it gets no arrows. The arrows on
                  the active rows above it are therefore always adjacent swaps. */}
              {row.isActive ? (
                <ReorderButtons
                  label={row.name}
                  disableUp={index === 0 || reorder.isPending}
                  disableDown={index === active.length - 1 || reorder.isPending}
                  onMove={(direction) => reorder.mutate({ id: row.id, direction })}
                />
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </td>
            <td className="px-4 py-3">
              <div className="font-medium">{row.name}</div>
              {/* The key is what the rest of the system stores and what every
                  past change record holds, so it is shown but never editable. */}
              <code className="text-xs text-muted-foreground">{row.key}</code>
            </td>
            <td className="px-4 py-3 text-muted-foreground">{row.personCount}</td>
            <td className="px-4 py-3">
              <StatusPill
                tone={row.isActive ? "success" : "neutral"}
                label={row.isActive ? "Active" : "Inactive"}
              />
            </td>
            <td className="px-4 py-3 text-right">
              <ActionCell
                items={[
                  {
                    icon: Pencil,
                    label: "Edit",
                    onClick: () => setDialog({ type: "edit", location: row }),
                  },
                  {
                    icon: Trash2,
                    label: "Delete",
                    onClick: () => setDialog({ type: "delete", location: row }),
                    variant: "destructive",
                    separatorBefore: true,
                    // Built-in locations and ones anyone still holds can
                    // only be deactivated — the server refuses either way, but
                    // there is no point offering an action that can't succeed.
                    disabled: isProtectedWorkLocationKey(row.key) || row.personCount > 0,
                  },
                ]}
              />
            </td>
          </tr>
        ))}
      </DataTable>

      {(dialog.type === "create" || dialog.type === "edit") && (
        <WorkLocationDialog
          open
          onOpenChange={() => setDialog({ type: "none" })}
          location={dialog.type === "edit" ? dialog.location : undefined}
        />
      )}

      {dialog.type === "delete" && (
        <ConfirmDialog
          open
          onOpenChange={() => setDialog({ type: "none" })}
          title={`Delete "${dialog.location.name}"?`}
          description="This removes the location from the list. It can only be deleted while nobody holds it — otherwise deactivate it instead, which hides it from the pickers but keeps it readable on past records."
          confirmLabel="Delete"
          destructive
          onConfirm={() => {
            deleteRow.mutate(dialog.location.id, {
              onSuccess: () => setDialog({ type: "none" }),
            });
          }}
        />
      )}
    </div>
  );
}

function WorkLocationDialog({
  open,
  onOpenChange,
  location,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  location?: WorkLocation;
}) {
  const isEdit = location != null;
  const create = useCreateWorkLocation();
  const update = useUpdateWorkLocation(location?.id ?? "");
  const form = useForm<WorkLocationCreateInput>({
    resolver: zodResolver(workLocationCreateSchema),
    defaultValues: {
      key: location?.key ?? "",
      name: location?.name ?? "",
      isActive: location?.isActive ?? true,
    },
  });

  async function onSubmit(values: WorkLocationCreateInput) {
    if (isEdit) {
      // `key` is deliberately not sent: it is immutable once created, because
      // it is stored on every person's record, on their history spans, and in
      // every past change record.
      await update.mutateAsync({
        name: values.name,
        isActive: values.isActive,
      });
    } else {
      await create.mutateAsync(values);
    }
    form.reset();
    onOpenChange(false);
  }

  const pending = create.isPending || update.isPending;

  return (
    <ConfigFormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? "Edit role location" : "Add role location"}
      description={
        isEdit
          ? "Update the label, or whether this location can still be chosen."
          : "Add a new work location people can be based in."
      }
      form={form}
      onSubmit={onSubmit}
      submitLabel={isEdit ? "Save" : "Create"}
      pending={pending}
    >
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Name</FormLabel>
            <FormControl>
              <Input placeholder="e.g. EMEA" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="key"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Key</FormLabel>
            <FormControl>
              <Input placeholder="e.g. emea" {...field} disabled={isEdit} />
            </FormControl>
            <FormDescription>
              {isEdit
                ? "The key can't be changed — it's stored on every person's record and in their change history."
                : "A permanent identifier used internally. Lowercase letters, numbers and underscores."}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="isActive"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start gap-3 space-y-0">
            <FormControl>
              <Checkbox checked={field.value ?? true} onCheckedChange={field.onChange} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>Available for selection</FormLabel>
              <FormDescription>
                Turn this off to retire the location. It disappears from the pickers but keeps
                rendering in history and on anyone who still holds it.
              </FormDescription>
            </div>
          </FormItem>
        )}
      />
    </ConfigFormDialog>
  );
}
